import { useState, useEffect, useRef } from "react";
import "./payrollSettings.css";
import "../../companyDetails.css";
import T from "utils/constants/T";
import MainCard from "components/MainCard";
import {
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useDispatch } from "react-redux";
import authApi from "interceptor/authInterceptor";
import {
  processXeroAccessToken,
} from "store/reducers/xero";
import { fetchCompanyDetails } from "store/reducers/companyTabDetails";
import toast from "utils/ToastNotistack";

interface ViewPayrollSettingsXeroProps {
  xeroAuthDetails: any;
  systemName: string;
  selectedCompanyId: string;
  role: string;
  handleSyncXero: () => void;
}

const ViewPayrollSettingsXero = ({
  xeroAuthDetails,
  systemName = "",
  selectedCompanyId = "",
  role = "",
  handleSyncXero,
}: ViewPayrollSettingsXeroProps) => {
  const dispatch = useDispatch();
  const [isReauthorizing, setIsReauthorizing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const checkPopupClosedRef = useRef<any>(null);
  const handleMessageRef = useRef<any>(null);

  const onSyncClick = async () => {
    setIsSyncing(true);
    try {
      await handleSyncXero();
    } catch (err: any) {
      toast(err?.response?.data?.message || err?.message || "Sync failed.", { variant: "error" });
    } finally {
      setIsSyncing(false);
    }
  };

  // Clean up timers and window event listeners on unmount
  useEffect(() => {
    return () => {
      if (checkPopupClosedRef.current) {
        clearInterval(checkPopupClosedRef.current);
      }
      if (handleMessageRef.current) {
        window.removeEventListener("message", handleMessageRef.current);
      }
    };
  }, []);

  const handleReauth = async () => {
    if (!selectedCompanyId) {
      toast("Please select a company first.", { variant: "error" });
      return;
    }

    setIsReauthorizing(true);
    try {
      const response = await authApi.get("/v2/api/xero/getOauthUrl?isReauth=true");
      const { oauthUrl } = response?.data?.data || {};

      if (oauthUrl) {
        const popup = window.open(
          oauthUrl,
          "xeroAuth",
          "resizable,scrollbars=0,status,top=100,left=200,width=800,height=400"
        );
        if (!popup) {
          toast("Popup blocked. Please allow popups and try again.", {
            variant: "error",
          });
          setIsReauthorizing(false);
          return;
        }
        popup?.focus();
        toast("Xero authorization opened successfully.", { variant: "success" });

        const handleMessage = async (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;

          const data = event.data;
          if (typeof data === "string" && data.includes("XEROCODE")) {
            const code = data.split("XEROCODE=")[1];
            window.removeEventListener("message", handleMessage);
            handleMessageRef.current = null;

            if (checkPopupClosedRef.current) {
              clearInterval(checkPopupClosedRef.current);
              checkPopupClosedRef.current = null;
            }

            try {
              // Dispatch token exchange action; backend handles token exchange, database persistence, and auditing
              await dispatch(
                (processXeroAccessToken as any)(code, true, selectedCompanyId)
              );

              toast("Xero authorization updated successfully.", { variant: "success" });
              // Refresh company details tab
              dispatch((fetchCompanyDetails as any)(selectedCompanyId));
            } catch (err: any) {
              toast(err?.response?.data?.message || err?.message || "Xero authentication failed.", {
                variant: "error",
              });
            } finally {
              setIsReauthorizing(false);
            }
          }
        };

        window.addEventListener("message", handleMessage);
        handleMessageRef.current = handleMessage;

        checkPopupClosedRef.current = setInterval(() => {
          if (!popup || popup.closed) {
            if (checkPopupClosedRef.current) {
              clearInterval(checkPopupClosedRef.current);
              checkPopupClosedRef.current = null;
            }
            setIsReauthorizing(false);
            window.removeEventListener("message", handleMessage);
            handleMessageRef.current = null;
          }
        }, 1000);
      } else {
        toast("Failed to generate Xero auth URL.", { variant: "error" });
        setIsReauthorizing(false);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to connect to Xero.";
      toast(message, { variant: "error" });
      setIsReauthorizing(false);
    }
  };

  const hasAccess = role === "superadmin" || role === "employer";

  return (
    <MainCard
      title={systemName || "Xero"}
      secondary={
        hasAccess && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              className="main-card-button"
              onClick={onSyncClick}
              disabled={isReauthorizing || isSyncing}
            >
              {isSyncing ? <CircularProgress size={20} color="inherit" /> : T.SYNC_PAYROLL}
            </Button>
            <Button
              variant="contained"
              className="main-card-button"
              onClick={handleReauth}
              disabled={isReauthorizing || isSyncing}
            >
              {isReauthorizing ? <CircularProgress size={20} color="inherit" /> : T.reAuth}
            </Button>
          </Stack>
        )
      }
    >
    </MainCard>
  );
};

export default ViewPayrollSettingsXero;
